/**
 * Stage I mouth layout — collect-band geometry for `checkSuction` (`layoutFallMouth` / `layoutFallCollectSlab`).
 * Band position/size use letterbox fractions (`y`, `w`, `h` of drawW / drawH), defaulting from
 * **`FALL_FLASK_COLLIDERS.collect`** (independent of Matter **`mouth`** walls).
 *
 * Edit `FALL_COLLECT_BAND` / `FALL_MOUTH_LID` below (or `runConfig.fallCollectBand` / `runConfig.fallMouthLid`).
 * Qualifiers: `FALL_COLLECT_RULES` / `runConfig.fallCollectSlab` overlap fields.
 * Fall collider highlights: `runtimeFlags.showFallColliderDebug` or `FALL_COLLIDER_DEBUG_OVERLAY.forceEnabled`.
 */
import { runtimeFlags } from "../core/runtime-flags.js";
import {
  FALL_FLASK_COLLIDERS,
  centeredRectWorld,
  drawFallFlaskShapeGuide,
  getFallFlaskColliderTuning,
} from "./fall-flask-shape.js";

/**
 * @typedef {Object} FallLetterboxBand
 * @property {number} y Top edge, fraction of drawH from drawY (same convention as flask colliders).
 * @property {number} w Interior width, fraction of drawW (centered on `cx`).
 * @property {number|null} [h] Band height, fraction of drawH. `null` on mouth-lid → wall thickness / drawH.
 */

/**
 * @typedef {Object} FallCollectRules
 * @property {number} minOverlapFrac Min horizontal overlap / object width (0.5 = 50%).
 * @property {number} nearBandDepthHalfHeights Reserved for near-band depth tuning.
 */

/** @type {FallLetterboxBand} */
export const FALL_COLLECT_BAND = {
  y: FALL_FLASK_COLLIDERS.collect.y,
  w: FALL_FLASK_COLLIDERS.collect.w,
  h: FALL_FLASK_COLLIDERS.collect.h,
};

/** @type {FallLetterboxBand} */
export const FALL_MOUTH_LID = {
  y: FALL_FLASK_COLLIDERS.mouth.y,
  w: FALL_FLASK_COLLIDERS.neck.w,
  h: null,
};

/** @type {FallCollectRules} */
export const FALL_COLLECT_RULES = {
  minOverlapFrac: 0.5,
  nearBandDepthHalfHeights: 4,
};

/**
 * @deprecated Use {@link FALL_COLLECT_BAND} + {@link FALL_COLLECT_RULES}.
 * @type {{ heightPx: number, rimOffsetDownPx: number, widthHalfExtraPx: number, minOverlapFrac: number, nearBandDepthHalfHeights: number }}
 */
export const FALL_COLLECT_SLAB_TUNING = {
  heightPx: 12,
  rimOffsetDownPx: 0,
  widthHalfExtraPx: 0,
  minOverlapFrac: FALL_COLLECT_RULES.minOverlapFrac,
  nearBandDepthHalfHeights: FALL_COLLECT_RULES.nearBandDepthHalfHeights,
};

/**
 * @deprecated Use {@link FALL_MOUTH_LID}.
 * @type {{ heightPx: number|null, rimOffsetDownPx: number, widthHalfExtraPx: number }}
 */
export const FALL_MOUTH_LID_TUNING = {
  heightPx: null,
  rimOffsetDownPx: 0,
  widthHalfExtraPx: 0,
};

/** Fall collider debug colors (flask Matter bodies, collect band, falling objects). */
export const FALL_COLLIDER_DEBUG_OVERLAY = {
  forceEnabled: false,
  flaskWallFill: "rgba(239, 68, 68, 0.4)",
  flaskWallStroke: "rgba(185, 28, 28, 0.95)",
  mouthFill: "rgba(239, 68, 68, 0.4)",
  mouthStroke: "rgba(185, 28, 28, 0.95)",
  /** @deprecated Use mouthFill */
  mouthLidFill: "rgba(239, 68, 68, 0.4)",
  /** @deprecated Use mouthStroke */
  mouthLidStroke: "rgba(185, 28, 28, 0.95)",
  dynamicBodyFill: "rgba(56, 189, 248, 0.32)",
  dynamicBodyStroke: "rgba(14, 116, 144, 0.85)",
  lineWidth: 1.5,
};

/** @returns {boolean} */
export function isFallColliderDebugOverlayEnabled() {
  if (typeof runtimeFlags.showFallColliderDebug === "boolean") {
    return runtimeFlags.showFallColliderDebug;
  }
  return !!FALL_COLLIDER_DEBUG_OVERLAY.forceEnabled;
}

var COLLECT_BAND_KEYS = ["y", "w", "h"];
var MOUTH_LID_BAND_KEYS = ["y", "w", "h"];
var RULE_KEYS = Object.keys(FALL_COLLECT_RULES);

function readNumber(value, fallback) {
  var n = Number(value);
  return isFinite(n) ? n : fallback;
}

function patchBandSection(target, patch, keys) {
  if (!patch || typeof patch !== "object") return;
  for (var i = 0; i < keys.length; i++) {
    var key = keys[i];
    if (patch[key] != null) {
      if (key === "h" && patch[key] === null) {
        target.h = null;
      } else {
        target[key] = readNumber(patch[key], target[key]);
      }
    }
  }
}

/**
 * @param {object} [runConfig]
 * @returns {FallLetterboxBand}
 */
export function getFallCollectBandTuning(runConfig) {
  var rc = runConfig || {};
  var coll = getFallFlaskColliderTuning(rc).collect || FALL_FLASK_COLLIDERS.collect;
  var out = {
    y: coll.y,
    w: coll.w,
    h: coll.h,
  };
  patchBandSection(out, rc.fallCollectBand, COLLECT_BAND_KEYS);
  var legacySlab = rc.fallCollectSlab;
  if (legacySlab && typeof legacySlab === "object") {
    patchBandSection(out, legacySlab, COLLECT_BAND_KEYS);
  }
  if (out.h != null && out.h <= 0) out.h = FALL_COLLECT_BAND.h;
  return out;
}

/**
 * @param {object} [runConfig]
 * @returns {FallLetterboxBand}
 */
export function getFallMouthLidBandTuning(runConfig) {
  var rc = runConfig || {};
  var out = {
    y: FALL_MOUTH_LID.y,
    w: FALL_MOUTH_LID.w,
    h: FALL_MOUTH_LID.h,
  };
  patchBandSection(out, rc.fallMouthLid, MOUTH_LID_BAND_KEYS);
  return out;
}

/**
 * Collection qualifiers (overlap fraction, etc.).
 * @param {object} [runConfig]
 * @returns {FallCollectRules}
 */
export function getFallCollectSlabTuning(runConfig) {
  var rc = runConfig || {};
  var out = Object.assign({}, FALL_COLLECT_RULES);
  var patch = rc.fallCollectSlab;
  if (patch && typeof patch === "object") {
    for (var i = 0; i < RULE_KEYS.length; i++) {
      var key = RULE_KEYS[i];
      if (patch[key] != null) out[key] = readNumber(patch[key], out[key]);
    }
  }
  if (out.minOverlapFrac <= 0 || out.minOverlapFrac > 1) {
    out.minOverlapFrac = FALL_COLLECT_RULES.minOverlapFrac;
  }
  if (out.nearBandDepthHalfHeights <= 0) {
    out.nearBandDepthHalfHeights = FALL_COLLECT_RULES.nearBandDepthHalfHeights;
  }
  return out;
}

/** @deprecated Use {@link getFallMouthLidBandTuning}. */
export function getFallMouthLidTuning(runConfig) {
  return getFallMouthLidBandTuning(runConfig);
}

/**
 * @param {{ drawX: number, drawY: number, drawW: number, drawH: number }} draw
 * @param {FallLetterboxBand} band
 * @param {number} wallThicknessPx
 * @returns {{ yFrac: number, wFrac: number, hFrac: number }}
 */
function resolveLetterboxBandFracs(draw, band, wallThicknessPx) {
  var dh = draw.drawH > 0 ? draw.drawH : 1;
  var dw = draw.drawW > 0 ? draw.drawW : 1;
  var yFrac = readNumber(band.y, 0);
  var wFrac = readNumber(band.w, 0);
  var hFrac = band.h != null && isFinite(band.h) && band.h > 0
    ? band.h
    : Math.max(1 / dh, readNumber(wallThicknessPx, 28) / dh);
  return { yFrac: yFrac, wFrac: wFrac, hFrac: hFrac };
}

/**
 * Legacy pixel offsets applied on top of letterbox band (deprecated `fallCollectSlab` keys).
 * @param {object} runConfig
 * @param {{ drawW: number, drawH: number }} draw
 * @returns {{ yExtraFrac: number, wExtraFrac: number, hOverrideFrac: number|null }}
 */
function legacyCollectSlabPixelAdjust(runConfig, draw) {
  var rc = runConfig || {};
  var dh = draw.drawH > 0 ? draw.drawH : 1;
  var dw = draw.drawW > 0 ? draw.drawW : 1;
  var hOverride = null;
  if (rc.FALL_COLLECT_SLAB_HEIGHT_PX != null) {
    hOverride = readNumber(rc.FALL_COLLECT_SLAB_HEIGHT_PX, 0) / dh;
  }
  var patch = rc.fallCollectSlab;
  if (patch && patch.heightPx != null && isFinite(patch.heightPx)) {
    hOverride = readNumber(patch.heightPx, 0) / dh;
  }
  var yExtra = 0;
  if (rc.FALL_COLLECT_SLAB_Y_OFFSET_PX != null) {
    yExtra = readNumber(rc.FALL_COLLECT_SLAB_Y_OFFSET_PX, 0) / dh;
  }
  if (patch && patch.rimOffsetDownPx != null) {
    yExtra = readNumber(patch.rimOffsetDownPx, 0) / dh;
  }
  var wExtra = 0;
  if (patch && patch.widthHalfExtraPx != null) {
    wExtra = (readNumber(patch.widthHalfExtraPx, 0) * 2) / dw;
  }
  return { yExtraFrac: yExtra, wExtraFrac: wExtra, hOverrideFrac: hOverride };
}

function rectToSlabLayout(rect) {
  var centerY = (rect.top + rect.bottom) / 2;
  return {
    mouthY: centerY,
    centerY: centerY,
    halfHeight: rect.height / 2,
    halfWidth: rect.halfW,
    top: rect.top,
    bottom: rect.bottom,
    width: rect.right - rect.left,
    height: rect.height,
  };
}

/**
 * @param {object} params
 * @param {number} params.cx
 * @param {{ drawX: number, drawY: number, drawW: number, drawH: number }} params.draw
 * @param {object} [params.runConfig]
 * @param {FallLetterboxBand} [params.band]
 * @param {number} [params.wallThicknessPx]
 */
export function layoutFallCollectSlab(params) {
  var cx = readNumber(params.cx, 0);
  var draw = params.draw;
  if (!draw || !(draw.drawW > 0) || !(draw.drawH > 0)) {
    return layoutFallCollectSlabLegacyPx(params);
  }
  var band = params.band || getFallCollectBandTuning(params.runConfig);
  var wallW = readNumber(params.wallThicknessPx, 28);
  var fracs = resolveLetterboxBandFracs(draw, band, wallW);
  var legacy = legacyCollectSlabPixelAdjust(params.runConfig, draw);
  fracs.yFrac += legacy.yExtraFrac;
  fracs.wFrac += legacy.wExtraFrac;
  if (legacy.hOverrideFrac != null && legacy.hOverrideFrac > 0) {
    fracs.hFrac = legacy.hOverrideFrac;
  }
  var rect = centeredRectWorld(cx, draw, fracs.yFrac, fracs.wFrac, fracs.hFrac);
  return rectToSlabLayout(rect);
}

/**
 * @deprecated Rim + px width path when letterbox `draw` is unavailable.
 */
function layoutFallCollectSlabLegacyPx(params) {
  var rimTopY = readNumber(params.rimTopY, 0);
  var mouthHalfW = Math.max(0, readNumber(params.mouthHalfWidthPx, 0));
  var drawH = 600;
  var hFrac = FALL_COLLECT_SLAB_TUNING.heightPx / drawH;
  var centerY = rimTopY + (hFrac * drawH) / 2;
  var halfH = (hFrac * drawH) / 2;
  return {
    mouthY: centerY,
    centerY: centerY,
    halfHeight: halfH,
    halfWidth: mouthHalfW,
    top: centerY - halfH,
    bottom: centerY + halfH,
    width: mouthHalfW * 2,
    height: halfH * 2,
  };
}

/**
 * @param {object} params
 * @param {number} params.cx
 * @param {{ drawX: number, drawY: number, drawW: number, drawH: number }} params.draw
 * @param {number} params.wallThicknessPx
 * @param {object} [params.runConfig]
 * @param {FallLetterboxBand} [params.band]
 */
export function layoutFallMouthLid(params) {
  var cx = readNumber(params.cx, 0);
  var draw = params.draw;
  var wallW = Math.max(1, readNumber(params.wallThicknessPx, 28));
  if (!draw || !(draw.drawW > 0) || !(draw.drawH > 0)) {
    return layoutFallMouthLidLegacyPx(params);
  }
  var band = params.band || getFallMouthLidBandTuning(params.runConfig);
  var fracs = resolveLetterboxBandFracs(draw, band, wallW);
  var legacy = legacyCollectSlabPixelAdjust(params.runConfig, draw);
  var patch = (params.runConfig && params.runConfig.fallMouthLid) || {};
  if (patch.rimOffsetDownPx != null) {
    fracs.yFrac += readNumber(patch.rimOffsetDownPx, 0) / draw.drawH;
  }
  if (patch.widthHalfExtraPx != null) {
    fracs.wFrac += (readNumber(patch.widthHalfExtraPx, 0) * 2) / draw.drawW;
  }
  if (patch.heightPx != null && isFinite(patch.heightPx) && patch.heightPx > 0) {
    fracs.hFrac = readNumber(patch.heightPx, 0) / draw.drawH;
  }
  fracs.yFrac += legacy.yExtraFrac;
  var rect = centeredRectWorld(cx, draw, fracs.yFrac, fracs.wFrac, fracs.hFrac);
  return rectToSlabLayout(rect);
}

function layoutFallMouthLidLegacyPx(params) {
  var rimTopY = readNumber(params.rimTopY, 0);
  var neckHalfW = Math.max(0, readNumber(params.neckHalfWidthPx, 0));
  var wallW = Math.max(1, readNumber(params.wallThicknessPx, 28));
  var tuning = getFallMouthLidBandTuning(params.runConfig);
  var patch = (params.runConfig && params.runConfig.fallMouthLid) || {};
  var height = patch.heightPx != null && patch.heightPx > 0
    ? readNumber(patch.heightPx, 0)
    : (tuning.h != null && tuning.h > 0 ? tuning.h * 600 : wallW);
  var halfH = height / 2;
  var halfW = neckHalfW + Math.max(0, readNumber(patch.widthHalfExtraPx, 0));
  var centerY = rimTopY + halfH + readNumber(patch.rimOffsetDownPx, 0);
  return {
    centerY: centerY,
    halfHeight: halfH,
    halfWidth: halfW,
    top: centerY - halfH,
    bottom: centerY + halfH,
    width: halfW * 2,
    height: height,
  };
}

/**
 * Unified mouth bar: union of boulder lid + `checkSuction` collect band.
 * @param {object} params
 * @param {number} params.cx
 * @param {{ drawX: number, drawY: number, drawW: number, drawH: number }} params.draw
 * @param {number} params.wallThicknessPx
 * @param {object} [params.runConfig]
 */
export function layoutFallMouth(params) {
  var lid = layoutFallMouthLid(params);
  var slab = layoutFallCollectSlab(params);
  var top = Math.min(lid.top, slab.top);
  var bottom = Math.max(lid.bottom, slab.bottom);
  var halfW = Math.max(lid.halfWidth, slab.halfWidth);
  var centerY = (top + bottom) / 2;
  var halfH = (bottom - top) / 2;
  return {
    mouthY: slab.mouthY,
    centerY: centerY,
    halfHeight: halfH,
    halfWidth: halfW,
    top: top,
    bottom: bottom,
    width: halfW * 2,
    height: bottom - top,
    lidLayout: lid,
    slabLayout: slab,
  };
}

function drawDebugMatterPolygon(ctx, cfg, verts, fill, stroke) {
  if (!verts || verts.length < 3) return;
  ctx.beginPath();
  ctx.moveTo(verts[0].x, verts[0].y);
  for (var i = 1; i < verts.length; i++) {
    ctx.lineTo(verts[i].x, verts[i].y);
  }
  ctx.closePath();
  ctx.fillStyle = fill;
  ctx.fill();
  if (stroke) {
    ctx.strokeStyle = stroke;
    ctx.lineWidth = cfg.lineWidth != null ? cfg.lineWidth : 1;
    ctx.stroke();
  }
}

function drawDebugMatterBody(ctx, cfg, body, fill, stroke) {
  if (!body) return;
  var parts = body.parts && body.parts.length > 1 ? body.parts.slice(1) : [body];
  for (var p = 0; p < parts.length; p++) {
    var part = parts[p];
    if (!part || !part.vertices || part.vertices.length < 3) continue;
    drawDebugMatterPolygon(ctx, cfg, part.vertices, fill, stroke);
  }
}

/**
 * Highlight Fall static flask colliders (red), shape guide, green collect band, and dynamic bodies (cyan).
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ flaskWalls?: object[], collectSlab?: object|null, getCollectSlabInfo?: function }|null|undefined} fallPhysics
 * @param {{ bodyEntries?: { body?: object }[] }} [opts]
 */
export function drawFallColliderDebug(ctx, fallPhysics, opts) {
  if (!isFallColliderDebugOverlayEnabled() || !ctx || !fallPhysics) return;
  var cfg = FALL_COLLIDER_DEBUG_OVERLAY;
  opts = opts || {};
  ctx.save();

  var walls = fallPhysics.flaskWalls;
  if (walls && walls.length) {
    for (var w = 0; w < walls.length; w++) {
      drawDebugMatterBody(ctx, cfg, walls[w], cfg.flaskWallFill, cfg.flaskWallStroke);
    }
  }

  if (fallPhysics.flaskGeometry) {
    drawFallFlaskShapeGuide(ctx, fallPhysics.flaskGeometry, {
      lineWidth: cfg.lineWidth,
    });
    var geom = fallPhysics.flaskGeometry;
    if (geom.drawW > 0 && geom.drawH > 0 && fallPhysics.getCollectSlabInfo) {
      var slab = fallPhysics.getCollectSlabInfo();
      if (slab) {
        ctx.strokeStyle = "rgba(34, 197, 94, 0.9)";
        ctx.lineWidth = cfg.lineWidth != null ? cfg.lineWidth : 1;
        ctx.strokeRect(
          slab.x - slab.halfWidth + 0.5,
          slab.top + 0.5,
          slab.halfWidth * 2 - 1,
          slab.bottom - slab.top - 1
        );
      }
    }
  }

  var entries = opts.bodyEntries;
  if (entries && entries.length) {
    for (var e = 0; e < entries.length; e++) {
      var entry = entries[e];
      if (!entry || !entry.body || entry.suction) continue;
      drawDebugMatterBody(ctx, cfg, entry.body, cfg.dynamicBodyFill, cfg.dynamicBodyStroke);
    }
  }

  ctx.restore();
}

/** @deprecated Use `drawFallColliderDebug`. */
export function drawFallMouthColliderDebug(ctx, fallPhysics, opts) {
  return drawFallColliderDebug(ctx, fallPhysics, opts);
}

/**
 * Horizontal overlap test for `checkSuction`.
 * @param {number} overlapPx
 * @param {number} objectHalfWidthPx
 * @param {FallCollectRules} tuning
 */
export function fallCollectSlabHasMinOverlap(overlapPx, objectHalfWidthPx, tuning) {
  var objHalfW = readNumber(objectHalfWidthPx, 0);
  if (!(objHalfW > 0)) return false;
  var minOverlap = objHalfW * (tuning.minOverlapFrac != null
    ? tuning.minOverlapFrac
    : FALL_COLLECT_RULES.minOverlapFrac);
  return overlapPx >= minOverlap;
}

/**
 * Vertical / swept collection test for `checkSuction`.
 * @param {object} params
 * @param {{ top: number, bottom: number }} params.slab
 * @param {number} params.currBottom
 * @param {number} params.currTop
 * @param {number|null} [params.prevBottom]
 * @param {FallCollectRules} tuning
 */
export function fallCollectSlabVerticalCollect(params, tuning) {
  var slab = params.slab;
  var currBottom = readNumber(params.currBottom, 0);
  var currTop = readNumber(params.currTop, currBottom);

  var bandOverlap = currTop <= slab.bottom && currBottom >= slab.top;
  var sweptCross = params.prevBottom != null
    && params.prevBottom < slab.top
    && currBottom >= slab.top;

  return bandOverlap || sweptCross;
}

/**
 * Full horizontal + vertical gate for mouth collection (`checkSuction`).
 * @param {object} params
 * @param {{ x: number, halfWidth: number, top: number, bottom: number }} params.slab
 * @param {{ min: { x: number, y: number }, max: { x: number, y: number } }} params.bounds
 * @param {number|null} [params.prevBottom]
 * @param {FallCollectRules} tuning
 */
export function fallCollectSlabQualifiesForCollection(params, tuning) {
  var slab = params.slab;
  var bounds = params.bounds;
  if (!slab || !bounds || !bounds.min || !bounds.max) return false;
  var objHalfW = (bounds.max.x - bounds.min.x) / 2;
  var overlap = Math.max(0,
    Math.min(bounds.max.x, slab.x + slab.halfWidth) -
    Math.max(bounds.min.x, slab.x - slab.halfWidth)
  );
  if (!fallCollectSlabHasMinOverlap(overlap, objHalfW, tuning)) return false;
  return fallCollectSlabVerticalCollect({
    slab: slab,
    currTop: bounds.min.y,
    currBottom: bounds.max.y,
    prevBottom: params.prevBottom,
  }, tuning);
}